import {
  mdiVideo,
  mdiImage,
  mdiFileDocument,
  mdiMusicNote,
  mdiFolder,
  mdiFile,
  mdiArchive,
  mdiPlaylistPlay,
  mdiSubtitles,
  mdiLinkVariant,
} from '@mdi/js'
import type { AssetType } from '@/api/assets/assets'

export interface AssetTypeConfig {
  icon: string
  color: string
  labelKey: string
}

// Keyed by the contract's words, so a type the contract adds is a type error here.
const assetTypeConfigs: Record<AssetType, AssetTypeConfig> = {
  video: { icon: mdiVideo, color: 'primary', labelKey: 'assets.assetTypes.video' },
  image: { icon: mdiImage, color: 'success', labelKey: 'assets.assetTypes.image' },
  document: { icon: mdiFileDocument, color: 'warning', labelKey: 'assets.assetTypes.document' },
  audio: { icon: mdiMusicNote, color: 'info', labelKey: 'assets.assetTypes.audio' },
  folder: { icon: mdiFolder, color: 'grey', labelKey: 'assets.assetTypes.folder' },
  collection: { icon: mdiPlaylistPlay, color: 'secondary', labelKey: 'assets.assetTypes.collection' },
  archive: { icon: mdiArchive, color: 'grey', labelKey: 'assets.assetTypes.archive' },
  file: { icon: mdiFile, color: 'grey', labelKey: 'assets.assetTypes.file' },
  subtitles: { icon: mdiSubtitles, color: 'info', labelKey: 'assets.assetTypes.subtitles' },
  third_party: { icon: mdiLinkVariant, color: 'error', labelKey: 'assets.assetTypes.thirdParty' },
  other: { icon: mdiFile, color: 'grey', labelKey: 'assets.assetTypes.other' },
}

function isAssetType(word: string): word is AssetType {
  return Object.prototype.hasOwnProperty.call(assetTypeConfigs, word)
}

/**
 * A type in the contract's spelling, whichever it arrives in: the contract's (`video`) or the
 * library's capitals (`VIDEO`). A word the contract does not carry reads as `other`.
 */
export function assetTypeKey(type: string): AssetType {
  const word = type.toLowerCase()
  return isAssetType(word) ? word : 'other'
}

/** A type's icon, colour and label, in whichever spelling it arrives (`assetTypeKey`). */
export function getAssetTypeConfig(type: string): AssetTypeConfig {
  return assetTypeConfigs[assetTypeKey(type)]
}

/** Every type's icon, by the contract's word. */
export function assetTypeIcons(): [AssetType, string][] {
  return (Object.keys(assetTypeConfigs) as AssetType[]).map((type) => [type, assetTypeConfigs[type].icon])
}

/** The same lookup, under the name the callers reading a wire payload use. */
export const getAssetTypeConfigFromString = getAssetTypeConfig
